/** 将镜像/制品 startupCommands.args 规范化为参数定义 */
export function normalizeStartupArgDefinitions(args: unknown): StartupArgDefinition[] {
  if (!args) return [];
  if (typeof args === 'string') {
    return args
      .split(',')
      .map((item) => item.trim())
      .filter(Boolean)
      .map((name) => ({
        name,
        optional: true,
        required: false,
        editable: true,
        visible: true,
        defaultValue: '',
      }));
  }
  if (!Array.isArray(args) || !args.length) return [];
  if (typeof args[0] === 'string') {
    return (args as string[])
      .map((name) => name?.trim())
      .filter(Boolean)
      .map((name) => ({
        name,
        optional: true,
        required: false,
        editable: true,
        visible: true,
        defaultValue: '',
      }));
  }
  return (args as StartupArgDefinition[])
    .map((item) => ({
      name: String(item.name ?? '').trim(),
      description: item.description?.trim() || undefined,
      defaultValue: item.defaultValue ?? '',
      optional: item.optional !== false,
      required: !!item.required,
      editable: item.editable !== false,
      visible: item.visible !== false,
    }))
    .filter((item) => item.name);
}

/** 启动参数定义（与制品 accessConfig.startupCommands.args 一致） */
export interface StartupArgDefinition {
  name: string;
  description?: string;
  defaultValue?: string;
  optional?: boolean;
  required: boolean;
  editable: boolean;
  visible: boolean;
}

/** 启动参数表单行 */
export interface StartupArgFormRow {
  name: string;
  value: string;
  _self?: string;
}

export type StartupArgMeta = StartupArgDefinition & { custom?: boolean };

/** 启动参数 API 存储：有值时为 name=value，无值时仅 name */
export function formatStartupArgToken(name: string, value: string): string | null {
  const n = String(name ?? '').trim();
  const v = String(value ?? '').trim();
  if (!n && !v) return null;
  if (!n) return v;
  if (!v) return n;
  return `${n}=${v}`;
}

export function parseStartupArgToken(token: string): { name: string; value: string } {
  const trimmed = String(token ?? '').trim();
  if (!trimmed) return { name: '', value: '' };
  const eq = trimmed.indexOf('=');
  if (eq === -1) return { name: trimmed, value: '' };
  const name = trimmed.slice(0, eq);
  const value = trimmed.slice(eq + 1);
  if (name) return { name, value };
  return { name: '', value: '' };
}

export function startupArgFormRowsToApiTokens(rows: StartupArgFormRow[]): string[] {
  return rows
    .map((row) => formatStartupArgToken(String(row?.name ?? ''), String(row?.value ?? '')))
    .filter((token): token is string => !!token);
}

export function apiTokensToStartupArgFormRows(tokens: string[]): StartupArgFormRow[] {
  return tokens
    .map((token) => {
      const { name, value } = parseStartupArgToken(token);
      if (!name && !value) return null;
      return {
        name,
        value,
        _self: JSON.stringify({
          name,
          optional: true,
          required: false,
          editable: true,
          visible: true,
          custom: true,
        } satisfies StartupArgMeta),
      };
    })
    .filter((row) => row != null);
}

export function initStartupArgFormRows(definitions: StartupArgDefinition[], existing?: unknown): StartupArgFormRow[] {
  if (Array.isArray(existing) && existing.length) {
    const first = existing[0];
    if (first && typeof first === 'object' && ('name' in first || 'value' in first)) {
      return (existing as StartupArgFormRow[]).map((row) => ({
        name: String(row.name ?? ''),
        value: String(row.value ?? ''),
        _self: row._self,
      }));
    }
    if (typeof first === 'string') {
      return apiTokensToStartupArgFormRows(existing as string[]);
    }
  }

  return definitions
    .filter((def) => def.visible !== false)
    .map((def) => ({
      name: def.name,
      value: def.defaultValue ?? '',
      _self: JSON.stringify(def),
    }));
}
